import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Buildings, Warning, Trash, Shield, Palette, Megaphone, UploadSimple, PencilSimple, ListDashes, Cube, ClipboardText, Lightning, SquaresFour, Article, FileText, Tag, CloudArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import NavigationBuilder from "@/pages/NavigationBuilder";
import ModuleBuilder from "@/pages/ModuleBuilder";
import FormBuilder from "@/pages/FormBuilder";
import WorkflowBuilder from "@/pages/WorkflowBuilder";
import UIBuilder from "@/pages/UIBuilder";
import TemplateBuilder from "@/pages/TemplateBuilder";
import CMSBuilder from "@/pages/CMSBuilder";
import LookupsBuilder from "@/pages/LookupsBuilder";
import PublishCenter from "@/pages/PublishCenter";

function StatGrid({ stats }) {
  const cards = [
    ["Firms", stats?.organizations], ["Users", stats?.users],
    ["Active users", stats?.active_users], ["Customers", stats?.customers],
    ["Leads", stats?.leads], ["Orders", stats?.orders],
    ["Quotations", stats?.quotations], ["Invoices", stats?.invoices],
    ["Payments", stats?.payments], ["Products", stats?.products],
    ["Files", stats?.files], ["Version", stats?.version],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {cards.map(([label, v]) => (
        <Card key={label} className="p-4">
          <div className="overline text-muted-foreground text-[10px]">{label}</div>
          <div className="font-heading font-bold text-2xl mt-1 leading-none">{v ?? "—"}</div>
        </Card>
      ))}
    </div>
  );
}

function LimitPill({ used, limit }) {
  const infinity = !limit || limit === 0;
  const pct = infinity ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const tone = infinity ? "text-muted-foreground" : pct >= 100 ? "text-destructive" : pct >= 80 ? "text-amber-600" : "text-emerald-700 dark:text-emerald-300";
  return (
    <span className={`text-xs font-mono ${tone}`}>
      {used} / {infinity ? "∞" : limit}
    </span>
  );
}

function LimitsDialog({ tenant, onClose, onSave }) {
  const [leadLimit, setLeadLimit] = useState(tenant?.lead_limit ?? 0);
  const [invoiceLimit, setInvoiceLimit] = useState(tenant?.invoice_limit ?? 0);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/platform/organizations/${tenant.id}/limits`, {
        lead_limit: +leadLimit, invoice_limit: +invoiceLimit,
      });
      toast.success("Limits updated");
      onSave();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!tenant} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="limits-dialog">
        <DialogHeader><DialogTitle>Limits · {tenant?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">LEAD LIMIT (0 = unlimited)</Label>
            <Input type="number" value={leadLimit} onChange={(e) => setLeadLimit(e.target.value)} data-testid="lead-limit-input" min={0} />
            <p className="text-[11px] text-muted-foreground mt-1">Current usage: {tenant?.lead_count ?? 0} leads</p>
          </div>
          <div>
            <Label className="text-xs">INVOICE LIMIT (0 = unlimited)</Label>
            <Input type="number" value={invoiceLimit} onChange={(e) => setInvoiceLimit(e.target.value)} data-testid="invoice-limit-input" min={0} />
            <p className="text-[11px] text-muted-foreground mt-1">Current usage: {tenant?.invoice_count ?? 0} invoices</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-limits">
            {saving ? "Saving…" : "Save limits"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FirmsTab({ orgs, reload }) {
  const [editing, setEditing] = useState(null);
  const suspend = async (t, flag) => {
    if (!window.confirm(`${flag ? "Deactivate" : "Reactivate"} "${t.name}"?`)) return;
    await api.put(`/platform/organizations/${t.id}/suspend`, null, { params: { suspend: flag } });
    toast.success(flag ? "Deactivated" : "Reactivated");
    reload();
  };
  const remove = async (t) => {
    if (!window.confirm(`⚠ Permanently delete "${t.name}" and ALL its data? This cannot be undone.`)) return;
    if (!window.confirm(`Type-check: are you absolutely sure?`)) return;
    await api.delete(`/platform/organizations/${t.id}`);
    toast.success("Firm deleted");
    reload();
  };
  return (
    <Card className="overflow-hidden">
      <div className="p-4 border-b border-border/40 flex items-center gap-2">
        <Buildings size={16} weight="duotone" className="text-primary" />
        <h3 className="font-heading font-bold">Firms ({orgs.length})</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
            <tr>
              <th className="text-left p-4">Firm</th>
              <th className="text-left p-4">Owner</th>
              <th className="text-left p-4">Users</th>
              <th className="text-left p-4">Leads</th>
              <th className="text-left p-4">Invoices</th>
              <th className="text-left p-4">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((t) => (
              <tr key={t.id} className="border-t border-border/40" data-testid={`org-${t.id}`}>
                <td className="p-4">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{t.id?.slice(0, 8)}</div>
                </td>
                <td className="p-4 text-muted-foreground">{t.owner_email}</td>
                <td className="p-4">{t.user_count}</td>
                <td className="p-4"><LimitPill used={t.lead_count} limit={t.lead_limit} /></td>
                <td className="p-4"><LimitPill used={t.invoice_count} limit={t.invoice_limit} /></td>
                <td className="p-4">
                  <span className={`text-xs px-2 py-1 rounded-full ${t.suspended ? "bg-destructive/10 text-destructive" : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"}`}>
                    {t.suspended ? "Deactivated" : "Active"}
                  </span>
                </td>
                <td className="p-4 text-right whitespace-nowrap">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEditing(t)} className="text-xs px-2 py-1 rounded border border-border hover:bg-secondary flex items-center gap-1" data-testid={`edit-limits-${t.id}`}>
                      <PencilSimple size={12} />Limits
                    </button>
                    <button onClick={() => suspend(t, !t.suspended)} className="text-xs px-2 py-1 rounded border border-border hover:bg-secondary" data-testid={`suspend-${t.id}`}>
                      {t.suspended ? "Reactivate" : "Deactivate"}
                    </button>
                    <button onClick={() => remove(t)} className="p-1.5 text-destructive hover:bg-destructive/10 rounded" data-testid={`delete-org-${t.id}`}><Trash size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <LimitsDialog tenant={editing} onClose={() => setEditing(null)} onSave={() => { setEditing(null); reload(); }} />}
    </Card>
  );
}

function BrandingTab({ settings, refresh }) {
  const [color, setColor] = useState(settings?.theme_color || "#4A2F1E");
  const [uploading, setUploading] = useState(null);
  const logoInput = useRef(null);
  const favInput = useRef(null);

  const uploadAsset = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/platform/upload", fd, {
        params: { kind },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${kind} uploaded`);
      refresh();
      return r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(null); }
  };

  const saveColor = async () => {
    try {
      await api.put("/platform/settings", { theme_color: color });
      toast.success("Theme color saved");
      refresh();
      // apply immediately
      document.documentElement.style.setProperty("--brand-color", color);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6" data-testid="branding-logo-card">
        <div className="overline text-muted-foreground mb-3 flex items-center gap-2"><UploadSimple size={12} />Company Logo</div>
        <div className="flex items-center gap-6">
          <div className="w-32 h-32 rounded-xl bg-secondary flex items-center justify-center overflow-hidden border border-border/60">
            {settings?.logo_url
              ? <img src={settings.logo_url} alt="logo" className="max-h-28 max-w-28 object-contain" />
              : <span className="text-xs text-muted-foreground">No logo</span>}
          </div>
          <div>
            <input ref={logoInput} type="file" accept="image/*" hidden onChange={(e) => uploadAsset("logo", e.target.files?.[0])} data-testid="upload-logo-input" />
            <Button variant="outline" onClick={() => logoInput.current?.click()} disabled={uploading === "logo"} data-testid="upload-logo-button">
              <UploadSimple size={14} className="mr-2" />
              {uploading === "logo" ? "Uploading…" : (settings?.logo_url ? "Replace logo" : "Upload logo")}
            </Button>
            <p className="text-xs text-muted-foreground mt-2 max-w-sm">PNG, JPG or SVG up to 5MB. Shown on the sign-in page and across the platform shell.</p>
          </div>
        </div>
      </Card>

      <Card className="p-6" data-testid="branding-favicon-card">
        <div className="overline text-muted-foreground mb-3 flex items-center gap-2"><UploadSimple size={12} />Favicon</div>
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 rounded-xl bg-secondary flex items-center justify-center overflow-hidden border border-border/60">
            {settings?.favicon_url
              ? <img src={settings.favicon_url} alt="favicon" className="max-h-14 max-w-14 object-contain" />
              : <span className="text-[10px] text-muted-foreground text-center">Default</span>}
          </div>
          <div>
            <input ref={favInput} type="file" accept="image/*,image/x-icon,.ico" hidden onChange={(e) => uploadAsset("favicon", e.target.files?.[0])} data-testid="upload-favicon-input" />
            <Button variant="outline" onClick={() => favInput.current?.click()} disabled={uploading === "favicon"} data-testid="upload-favicon-button">
              <UploadSimple size={14} className="mr-2" />
              {uploading === "favicon" ? "Uploading…" : (settings?.favicon_url ? "Replace favicon" : "Upload favicon")}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">32×32 or 64×64 PNG / ICO recommended.</p>
          </div>
        </div>
      </Card>

      <Card className="p-6" data-testid="branding-color-card">
        <div className="overline text-muted-foreground mb-3 flex items-center gap-2"><Palette size={12} />Primary Theme Color</div>
        <div className="flex items-end gap-4">
          <div>
            <Label className="text-xs">HEX COLOR</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-10 w-14 rounded border border-border cursor-pointer" data-testid="theme-color-picker" />
              <Input value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} className="w-32 font-mono" data-testid="theme-color-input" />
            </div>
          </div>
          <div className="flex gap-2">
            {["#4A2F1E", "#0F172A", "#1E40AF", "#059669", "#DB2777", "#EA580C"].map((c) => (
              <button key={c} onClick={() => setColor(c)} className="w-8 h-8 rounded-full border-2 border-border hover:scale-110 transition-transform" style={{ background: c }} data-testid={`swatch-${c.slice(1)}`} />
            ))}
          </div>
          <Button onClick={saveColor} data-testid="save-theme-color">Save color</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">The color drives primary buttons, links and highlights across the sign-in page and any anonymous pages.</p>
      </Card>
    </div>
  );
}

function ContentTab({ settings, refresh }) {
  const [headline, setHeadline] = useState(settings?.homepage_headline || "");
  const [announcement, setAnnouncement] = useState(settings?.announcement_text || "");
  const [active, setActive] = useState(!!settings?.announcement_active);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/platform/settings", {
        homepage_headline: headline,
        announcement_text: announcement,
        announcement_active: active,
      });
      toast.success("Content saved");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-6">
      <Card className="p-6" data-testid="content-headline-card">
        <div className="overline text-muted-foreground mb-3">Homepage Headline</div>
        <Textarea rows={2} value={headline} onChange={(e) => setHeadline(e.target.value)}
          placeholder="Every deal, every rupee, tracked." data-testid="homepage-headline" className="text-lg" />
        <p className="text-[11px] text-muted-foreground mt-2">Shown as the main marketing headline on the sign-in / landing screen.</p>
      </Card>

      <Card className="p-6" data-testid="content-announcement-card">
        <div className="flex items-center justify-between mb-3">
          <div className="overline text-muted-foreground flex items-center gap-2"><Megaphone size={12} />Announcement Bar</div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={active} onCheckedChange={setActive} data-testid="announcement-toggle" />
            <span>{active ? "Visible" : "Hidden"}</span>
          </label>
        </div>
        <Textarea rows={2} value={announcement} onChange={(e) => setAnnouncement(e.target.value)}
          placeholder="🎉 New feature: WhatsApp Business is live for all workspaces."
          data-testid="announcement-text" />
        <p className="text-[11px] text-muted-foreground mt-2">Appears as a thin bar at the top of every authenticated page. Keep it under ~120 characters.</p>
      </Card>

      <Button onClick={save} disabled={saving} className="rounded-full" data-testid="save-content">
        {saving ? "Saving…" : "Save content"}
      </Button>
    </div>
  );
}

export default function PlatformAdmin() {
  const { user } = useAuth();
  const [allowed, setAllowed] = useState(null);
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/platform/whoami").then((r) => setAllowed(r.data.is_platform_admin))
      .catch(() => setAllowed(false));
  }, []);

  const reload = () => {
    api.get("/platform/organizations").then((r) => setOrgs(r.data));
    api.get("/platform/stats").then((r) => setStats(r.data));
    api.get("/platform/settings").then((r) => setSettings(r.data));
  };

  useEffect(() => { if (allowed) reload(); }, [allowed]);  // eslint-disable-line

  if (allowed === null) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!allowed) return (
    <div className="p-8 max-w-lg" data-testid="platform-forbidden">
      <Card className="p-6 flex items-start gap-3">
        <Warning size={22} weight="duotone" className="text-destructive shrink-0 mt-0.5" />
        <div>
          <div className="font-heading font-bold">Not found</div>
          <div className="text-sm text-muted-foreground mt-1">This page does not exist.</div>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="platform-page">
      <div>
        <div className="overline text-muted-foreground mb-2 flex items-center gap-2"><Shield size={12} weight="fill" /> Platform Admin</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Platform Admin Console</h1>
        <p className="text-sm text-muted-foreground mt-2">Signed in as <span className="font-mono text-xs">{user?.email}</span>. Restricted to platform admins — hidden from all organizations.</p>
      </div>

      <StatGrid stats={stats} />

      <Tabs defaultValue="firms">
        <TabsList className="w-full md:w-auto" data-testid="platform-tabs">
          <TabsTrigger value="firms" data-testid="tab-firms"><Buildings size={14} className="mr-1.5" />Firms & Limits</TabsTrigger>
          <TabsTrigger value="navigation" data-testid="tab-navigation"><ListDashes size={14} className="mr-1.5" />Navigation</TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules"><Cube size={14} className="mr-1.5" />Modules</TabsTrigger>
          <TabsTrigger value="forms" data-testid="tab-forms"><ClipboardText size={14} className="mr-1.5" />Forms</TabsTrigger>
          <TabsTrigger value="workflows" data-testid="tab-workflows"><Lightning size={14} className="mr-1.5" />Workflows</TabsTrigger>
          <TabsTrigger value="ui" data-testid="tab-ui"><SquaresFour size={14} className="mr-1.5" />UI Builder</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates"><Article size={14} className="mr-1.5" />Templates</TabsTrigger>
          <TabsTrigger value="cms" data-testid="tab-cms"><FileText size={14} className="mr-1.5" />CMS</TabsTrigger>
          <TabsTrigger value="lookups" data-testid="tab-lookups"><Tag size={14} className="mr-1.5" />Lookups</TabsTrigger>
          <TabsTrigger value="publish" data-testid="tab-publish"><CloudArrowUp size={14} className="mr-1.5" />Publish</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding"><Palette size={14} className="mr-1.5" />Branding</TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content"><Megaphone size={14} className="mr-1.5" />App Content</TabsTrigger>
        </TabsList>
        <TabsContent value="firms" className="mt-6"><FirmsTab orgs={orgs} reload={reload} /></TabsContent>
        <TabsContent value="navigation" className="mt-6"><NavigationBuilder /></TabsContent>
        <TabsContent value="modules" className="mt-6"><ModuleBuilder /></TabsContent>
        <TabsContent value="forms" className="mt-6"><FormBuilder /></TabsContent>
        <TabsContent value="workflows" className="mt-6"><WorkflowBuilder /></TabsContent>
        <TabsContent value="ui" className="mt-6"><UIBuilder /></TabsContent>
        <TabsContent value="templates" className="mt-6"><TemplateBuilder /></TabsContent>
        <TabsContent value="cms" className="mt-6"><CMSBuilder /></TabsContent>
        <TabsContent value="lookups" className="mt-6"><LookupsBuilder /></TabsContent>
        <TabsContent value="publish" className="mt-6"><PublishCenter /></TabsContent>
        <TabsContent value="branding" className="mt-6">{settings && <BrandingTab settings={settings} refresh={reload} />}</TabsContent>
        <TabsContent value="content" className="mt-6">{settings && <ContentTab settings={settings} refresh={reload} />}</TabsContent>
      </Tabs>
    </div>
  );
}
